import React, { useEffect, useState } from "react";
import { Checkbox } from "@progress/kendo-react-inputs";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { useTranslation } from "react-i18next";
import { useComponentProperties, useValidation } from "../../../../Hooks/Display/useComponentProperties";
import { useSelectedComponent } from "../../../../Hooks/Display/useComponents";
import { usePropsStoreValue } from "../../../../Hooks/PropStore/usePropsStore";
import { getAttributes } from "../../../../API/Attribute/Attribute";
import ErrorMessage from "../../../Shared/Error/ErrorMessage";
import { useSelectedPage } from "../../../../Hooks/Display/usePages";

const DataFilter = () => {
    const { t } = useTranslation("display/components/data_filter");
    const selectedComponent = useSelectedComponent();
    const selectedPage = useSelectedPage();
    const entityId = usePropsStoreValue("entityId");
    const [properties, setProperty] = useComponentProperties();
    const [, setValidationErrors] = useValidation();

    const [textAndDateAttributes, setTextAndDateAttributes] = useState([]);
    const [filterLabelError, setFilterLabelError] = useState("");
    const [filterAttributesError, setFilterAttributesError] = useState("");

    // Helper function to ensure filterAttributes is always an array
    const getFilterAttributesArray = () => {
        const value = properties.filterAttributes;
        if (Array.isArray(value)) {
            return value;
        }
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        return [];
    };

    // Helper function to get full attribute objects for display from stored IDs
    const getFilterAttributesForDisplay = () => {
        const attributeIds = getFilterAttributesArray();
        if (attributeIds.length === 0) {
            return [];
        }
        // Map the stored IDs back to full attribute objects for display
        return attributeIds.map(id =>
            textAndDateAttributes.find(attr => attr._id === id)
        ).filter(attr => attr !== undefined); // Remove any undefined values
    };

    // Initialize filterAttributes as array if it's not already
    useEffect(() => {
        if (properties.filterAttributes !== undefined && !Array.isArray(properties.filterAttributes)) {
            setProperty("filterAttributes", []);
        }
    }, [properties.filterAttributes]);

    // Fetch attributes for Field 3 dropdown
    useEffect(() => {
        const fetchAttributes = async () => {
            if (entityId) {
                const attributes = await getAttributes(entityId);
                const filteredAttributes = attributes.filter(attribute => {
                    // Check if this is Cards, Table, or List component
                    const isNormalCardsTableOrList = selectedComponent &&
                        (selectedComponent.componentType === "Cards" ||
                            selectedComponent.componentType === "Table" ||
                            selectedComponent.componentType === "List") &&
                        !selectedPage.useAsReference;

                    if (isNormalCardsTableOrList) {
                        // For Cards, Table, and List: Include Text, Date, Dropdown, Mirrored Dropdown, and Shared Dropdown
                        return (
                            attribute.attributeType === "String" ||
                            (attribute.attributeType === "Mirror" && attribute.attribute.attributeType === "String") ||
                            (attribute.attributeType === "DateTime" && attribute.dateTimeType !== "time") ||
                            (attribute.attributeType === "Mirror" && attribute.attribute.attributeType === "DateTime" && attribute.attribute.dateTimeType !== "time") ||
                            attribute.attributeType === "Picklist String" ||
                            (attribute.attributeType === "Mirror" && attribute.attribute.attributeType === "Picklist String") ||
                            (attribute.attributeType === "Shared Lookup" && attribute.sharedLookupType === "Picklist String")
                        );
                    } else {
                        // For other components: Keep original behavior (only Text and Date)
                        return (
                            attribute.attributeType === "String" ||
                            (attribute.attributeType === "Mirror" && attribute.attribute.attributeType === "String") ||
                            (attribute.attributeType === "DateTime" && attribute.dateTimeType !== "time") ||
                            (attribute.attributeType === "Mirror" && attribute.attribute.attributeType === "DateTime" && attribute.attribute.dateTimeType !== "time")
                        );
                    }
                }).sort((a, b) => a.attributeName.localeCompare(b.attributeName));
                setTextAndDateAttributes(filteredAttributes);
            }
        };

        if (entityId && selectedComponent && selectedPage) {
            fetchAttributes();
        }
    }, [entityId, selectedComponent, selectedPage]);

    // Handle Data Filter checkbox change
    const handleShowFilterChange = (event) => {
        const isEnabled = event.target.value;
        setProperty("showFilter", isEnabled);

        // Reset Filter Label and Filter Attributes when Data Filter is turned off
        if (!isEnabled) {
            setProperty("filterLabel", "");
            setProperty("filterAttributes", []);
            setFilterLabelError("");
            setFilterAttributesError("");
        } else {
            // Clear errors when Data Filter is turned on
            setFilterLabelError("");
            setFilterAttributesError("");
        }
    };

    // Handle Filter Label text input change
    const handleFilterLabelChange = (event) => {
        const value = event.target.value;
        // Limit to 128 characters
        if (value.length <= 128) {
            setProperty("filterLabel", value);
            setFilterLabelError("");
        }
    };

    // Handle Filter Attributes multi-select change
    const handleFilterAttributesChange = (event) => {
        const value = event.target.value;
        // Extract only the IDs from the selected attribute objects
        const attributeIds = Array.isArray(value) ? value.map(attr => attr._id) : [];
        setProperty("filterAttributes", attributeIds);
        setFilterAttributesError("");
    };

    // Validate fields whenever properties change
    useEffect(() => {
        const errors = {};

        if (properties.showFilter) {
            // Validate filter label
            if (!properties.filterLabel || properties.filterLabel.trim() === "") {
                setFilterLabelError(t("validation_messages.required_field"));
                errors.filterLabel = t("validation_messages.required_field");
            } else {
                setFilterLabelError("");
            }

            // Validate filter attributes
            const value = properties.filterAttributes;
            let filterAttributesArray = [];
            if (Array.isArray(value)) {
                filterAttributesArray = value;
            } else if (typeof value === 'string') {
                try {
                    const parsed = JSON.parse(value);
                    filterAttributesArray = Array.isArray(parsed) ? parsed : [];
                } catch {
                    filterAttributesArray = [];
                }
            }

            if (!filterAttributesArray || filterAttributesArray.length === 0) {
                setFilterAttributesError(t("validation_messages.required_field"));
                errors.filterAttributes = t("validation_messages.required_field");
            } else if (filterAttributesArray.length > 5) {
                setFilterAttributesError(t("validation_messages.max_5_attributes"));
                errors.filterAttributes = t("validation_messages.max_5_attributes");
            } else {
                setFilterAttributesError("");
            }
        } else {
            setFilterLabelError("");
            setFilterAttributesError("");
        }

        // Update global validation state
        setValidationErrors(errors);
    }, [properties.showFilter, properties.filterLabel, properties.filterAttributes]);

    return (
        <div className="data-filter-container">
            {/* Data Filter: Checkbox */}
            <Checkbox
                name="showFilter"
                disabled={selectedComponent.hidden}
                checked={properties.showFilter || false}
                onChange={handleShowFilterChange}
                label={t("form_labels.data_filter")}
                className="my-1 d-block"
            />

            {/* Filter Label and Filter Attributes: Only show when Data Filter is true */}
            {properties.showFilter && (
                <>
                    {/* Data Filter Label: Text input */}
                    <div className="form-label mt-2">
                        {t("form_labels.filter_label")}
                        <ErrorMessage
                            valid={!filterLabelError}
                            error={filterLabelError}
                        />
                    </div>
                    <input
                        className="form-control w-50"
                        type="text"
                        name="filterLabel"
                        disabled={selectedComponent.hidden}
                        value={properties.filterLabel || ""}
                        onChange={handleFilterLabelChange}
                        placeholder={t("form_placeholders.filter_label")}
                        maxLength={128}
                    />

                    {/* Filter Properties: Multi-select */}
                    <div className="form-label mt-2">
                        {t("form_labels.filter_properties")}
                        <ErrorMessage
                            valid={!filterAttributesError}
                            error={filterAttributesError}
                        />
                    </div>
                    <MultiSelect
                        name="filterAttributes"
                        disabled={selectedComponent.hidden}
                        value={getFilterAttributesForDisplay()}
                        onChange={handleFilterAttributesChange}
                        className="form-control-custom w-50"
                        data={textAndDateAttributes}
                        textField="attributeName"
                        dataItemKey="_id"
                        placeholder={t("form_placeholders.filter_properties")}
                    />
                </>
            )}
        </div>
    );
};

export default DataFilter;
